import { ElementRef } from '@angular/core';
import { ChatLoggingService } from '../services/chat-logging.service';
import moment from 'moment';

export class AudioHelpers {
  static convertDataURIToBinary(dataURI) {
    var BASE64_MARKER = ';base64,';
    var base64Index = dataURI.indexOf(BASE64_MARKER) + BASE64_MARKER.length;
    var base64 = dataURI.substring(base64Index);
    var raw = window.atob(base64);
    var rawLength = raw.length;
    var array = new Uint8Array(new ArrayBuffer(rawLength));

    for (let i = 0; i < rawLength; i++) {
      array[i] = raw.charCodeAt(i);
    }
    return array;
  }

  static audioUpdateTime(audioEl: ElementRef, audioObj: any) {
    let audio = audioEl.nativeElement;
    var audioDuration = 0;
    if (audio.duration != Infinity && !isNaN(audio.duration)) {
      audioDuration = audio.duration;
    }
    if (audioDuration) audioObj.audioPlaying = true;
    //From what I understand, formatting this way will cause it to roll over at 59:99
    //Don't think that'll be an issue for this current use case, but something to be aware of
    audioObj.audioEnd = moment.utc(moment.duration(audioDuration, 'seconds').asMilliseconds()).format('mm:ss');
    audioObj.audioStart = moment.utc(moment.duration(audio.currentTime, 'seconds').asMilliseconds()).format('mm:ss');
    audioObj.audioProgress = (audio.currentTime / audioDuration) * 100 + '%';
    if (audioObj.audioProgress == 'NaN%') {
      audioObj.audioProgress = '0%';
    }
    if (audioObj.audioProgress == '100%') {
      audioObj.audioPlaying = false;
    }
  }

  static drawAudio(
    url: string,
    audioContext: AudioContext,
    chatLoggingService: ChatLoggingService,
    componentName: string,
    waveformsCanvas: ElementRef,
    progressCanvas: ElementRef,
    color: string,
    attachmentId?: string
  ) {
    fetch(url)
      .then(response => response.arrayBuffer())
      .then(arrayBuffer =>
        audioContext.decodeAudioData(
          arrayBuffer,
          audioBuffer => {
            this.draw(
              this.normalizeData(this.filterData(audioBuffer, waveformsCanvas)),
              waveformsCanvas,
              progressCanvas,
              color
            );
          },
          error => {
            chatLoggingService.error({
              logMessage: `${componentName}.component.drawAudio() - Error with decoding audio data ${error.message}`,
              logParameters: [attachmentId, error]
            });
          }
        )
      );
  }

  static filterData(audioBuffer, waveformsCanvas) {
    const rawData = audioBuffer.getChannelData(0); // We only need to work with one channel of data
    waveformsCanvas.nativeElement.style.width = '100%';
    let samples = Math.floor(waveformsCanvas.nativeElement.offsetWidth / 6); // Number of samples we want to have in our final data set
    const blockSize = Math.floor(rawData.length / samples); // the number of samples in each subdivision
    const filteredData = [];
    for (let i = 0; i < samples; i++) {
      let blockStart = blockSize * i; // the location of the first sample in the block
      let sum = 0;
      for (let j = 0; j < blockSize; j++) {
        sum = sum + Math.abs(rawData[blockStart + j]); // find the sum of all the samples in the block
      }
      filteredData.push(sum / blockSize); // divide the sum by the block size to get the average
    }
    return filteredData;
  }

  static normalizeData(filteredData) {
    const multiplier = Math.pow(Math.max(...filteredData), -1);
    return filteredData.map(n => n * multiplier);
  }

  static draw(normalizedData, waveformsCanvas, progressCanvas, color) {
    const canvas = waveformsCanvas.nativeElement;
    canvas.innerHTML = '';
    let ctx = this.scaleCanvas(canvas);
    ctx.translate(0, canvas.offsetHeight / 2);
    const width = canvas.offsetWidth / normalizedData.length;
    for (let i = 0; i < normalizedData.length; i++) {
      const x = width * i;
      let height = normalizedData[i] * canvas.offsetHeight;
      if (height < 0) {
        height = 0;
      } else if (height > canvas.offsetHeight / 2) {
        height = height - canvas.offsetHeight / 2;
      }
      this.drawLineSegment(ctx, x, height, color);
    }
    let canvasClone = this.cloneCanvas(canvas);
    progressCanvas.nativeElement.innerHTML = '';
    progressCanvas.nativeElement.appendChild(canvasClone);
  }

  static drawLineSegment(ctx, x, height, color) {
    ctx.lineWidth = 2;
    ctx.strokeStyle = color;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x, -height);
    ctx.lineTo(x, height);
    ctx.stroke();
  }

  static scaleCanvas(canvas: HTMLCanvasElement, oldCanvas?: HTMLCanvasElement) {
    let canvasWidth = oldCanvas ? oldCanvas.offsetWidth : canvas.offsetWidth;
    let canvasHeight = 19; //should be the same as  $audio-visualization-height in scss file
    let scale = window.devicePixelRatio || 1;
    canvas.style.width = canvasWidth / 14 + 'rem';
    canvas.style.height = canvasHeight / 14 + 'rem';
    const ctx = canvas.getContext('2d');
    canvas.width = Math.floor(canvasWidth * scale);
    canvas.height = Math.floor(canvasHeight * scale);
    // Normalize coordinate system to use css pixels.
    ctx.scale(scale, scale);
    return ctx;
  }

  static cloneCanvas(oldCanvas) {
    let newCanvas = document.createElement('canvas');
    let context = this.scaleCanvas(newCanvas, oldCanvas);
    newCanvas.width = oldCanvas.width;
    newCanvas.height = oldCanvas.height;
    context.drawImage(oldCanvas, 0, 0);
    return newCanvas;
  }
}
